import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../../components/ui';
import {
  TrendingUp, AlertTriangle, RefreshCw, Activity,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { fechaLocalYMD } from '../../../utils/date';
import {
  fetchGanancia,
  fetchRiesgo,
  fetchAlertas,
  GananciaData,
  RiesgoNumero,
  Alerta,
} from '../services/analiticaService';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatColones(value: number): string {
  return `₡${Math.round(value).toLocaleString('es-CR')}`;
}

function nivelColor(nivel: 'BAJO' | 'MEDIO' | 'ALTO'): string {
  if (nivel === 'ALTO') return 'text-red-700 bg-red-50 border-red-200';
  if (nivel === 'MEDIO') return 'text-yellow-700 bg-yellow-50 border-yellow-200';
  return 'text-green-700 bg-green-50 border-green-200';
}

function nivelBadge(nivel: 'BAJO' | 'MEDIO' | 'ALTO'): string {
  if (nivel === 'ALTO') return 'bg-red-100 text-red-800';
  if (nivel === 'MEDIO') return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
}

// ── Componentes de tarjeta de resumen ────────────────────────────────────────

interface ResumenCardProps {
  titulo: string;
  valor: string;
  positivo?: boolean | null;
  icono: React.ReactNode;
}

const ResumenCard: React.FC<ResumenCardProps> = ({ titulo, valor, positivo, icono }) => {
  const TrendIcon =
    positivo === true ? ArrowUpRight :
    positivo === false ? ArrowDownRight :
    Minus;
  const trendClass =
    positivo === true ? 'text-green-500' :
    positivo === false ? 'text-red-500' :
    'text-gray-400';

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-500 truncate">{titulo}</p>
          <p className="text-xl font-bold text-gray-900 mt-1 truncate">{valor}</p>
        </div>
        <div className="flex flex-col items-center gap-1 ml-3">
          <div className="text-gray-400">{icono}</div>
          <TrendIcon size={14} className={trendClass} />
        </div>
      </div>
    </Card>
  );
};

// ── Página principal ─────────────────────────────────────────────────────────

export const AnaliticaDashboard: React.FC = () => {
  const [fecha, setFecha] = useState<string>(fechaLocalYMD());
  const [ganancia, setGanancia] = useState<GananciaData | null>(null);
  const [riesgo, setRiesgo] = useState<RiesgoNumero[]>([]);
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargarDatos = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchGanancia(fecha),
      fetchRiesgo(undefined, fecha),
      fetchAlertas(fecha),
    ])
      .then(([g, r, a]) => {
        setGanancia(g);
        setRiesgo(r);
        setAlertas(a);
      })
      .catch(err => setError(err?.message ?? 'Error cargando analítica'))
      .finally(() => setLoading(false));
  }, [fecha]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const numerosAlto = riesgo.filter(n => n.nivel === 'ALTO').length;
  const numerosMedio = riesgo.filter(n => n.nivel === 'MEDIO').length;
  const alertasAlto = alertas.filter(a => a.nivel === 'ALTO').length;

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity size={24} className="text-blue-600" />
            Control Inteligente
          </h1>
          <p className="text-gray-500 text-sm mt-1">Analítica de ganancia y riesgo en tiempo real</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
          <button
            onClick={cargarDatos}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Alertas destacadas */}
      {alertas.length > 0 && (
        <div className="space-y-2">
          {alertas.map((a, i) => (
            <div
              key={i}
              className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${nivelColor(a.nivel)}`}
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{a.mensaje}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tarjetas de ganancia */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <TrendingUp size={16} />
          Resumen financiero del día
        </h2>
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : ganancia ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <ResumenCard
              titulo="Ventas"
              valor={formatColones(ganancia.ventas)}
              positivo={ganancia.ventas > 0}
              icono={<TrendingUp size={20} />}
            />
            <ResumenCard
              titulo="Premios pagados"
              valor={formatColones(ganancia.premios)}
              positivo={null}
              icono={<ArrowDownRight size={20} />}
            />
            <ResumenCard
              titulo="Comisiones"
              valor={formatColones(ganancia.comisiones)}
              positivo={null}
              icono={<Minus size={20} />}
            />
            <ResumenCard
              titulo="Ganancia neta"
              valor={formatColones(ganancia.ganancia)}
              positivo={ganancia.ganancia >= 0}
              icono={<Activity size={20} />}
            />
          </div>
        ) : null}
      </section>

      {/* Tabla de riesgo por número */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <AlertTriangle size={16} />
          Exposición por número
          {riesgo.length > 0 && (
            <span className="text-xs font-normal text-gray-500">
              — {numerosAlto} alto, {numerosMedio} medio
            </span>
          )}
        </h2>

        {loading ? (
          <div className="h-48 bg-gray-100 rounded-xl animate-pulse" />
        ) : riesgo.length === 0 ? (
          <Card className="p-6 text-center text-gray-500 text-sm">
            Sin jugadas activas para esta fecha.
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">#</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Número</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Sorteo</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Vendido</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Mult.</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Riesgo</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Nivel</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {riesgo.slice(0, 50).map((n, i) => (
                    <tr key={`${n.numero}-${n.sorteo_id ?? i}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2 font-mono font-bold text-gray-900">{n.numero}</td>
                      <td className="px-4 py-2 text-gray-600 text-xs">
                        {n.sorteo_nombre ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-700">
                        {formatColones(n.monto_vendido)}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-500">{n.multiplicador}x</td>
                      <td className="px-4 py-2 text-right font-semibold">
                        <span className={n.nivel === 'ALTO' ? 'text-red-700' : n.nivel === 'MEDIO' ? 'text-yellow-700' : 'text-gray-700'}>
                          {formatColones(n.riesgo)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${nivelBadge(n.nivel)}`}>
                          {n.nivel}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {riesgo.length > 50 && (
                <div className="px-4 py-3 text-xs text-gray-400 border-t border-gray-100">
                  Mostrando 50 de {riesgo.length} números ordenados por riesgo.
                </div>
              )}
            </div>
          </Card>
        )}
      </section>
    </div>
  );
};

export default AnaliticaDashboard;
